import type { UiAdapter } from '../adapters/ui-adapter';
import { configureAmplify } from '../adapters/amplify-client';
import { uiAdapter } from '../adapters/ui-adapter';
import {
  createTriedgeParityBridge,
  type TriedgeParityBridge,
} from './parity';
export type {
  TriedgeParityBridge,
  TriedgeParityBridgeImplementation,
  TriedgeParityQueryState,
  TriedgeParityRouteReadiness,
  TriedgeParityRouteStatus,
} from './parity';

export interface TriedgeBridgeGlobalConfig {
  environment: 'sandbox' | 'production';
  region: string;
  userPoolId: string;
  userPoolClientId?: string;
  graphqlEndpoint: string;
  academicYear?: string;
  useLocalStore?: boolean;
  logEvents?: boolean;
}

export interface TriedgeBridgeHealth {
  loadedAt: string;
  configuredAt?: string;
  configured: boolean;
  ready: boolean;
  uiAdapterReady: boolean;
  version: string;
}

export type TriedgeBridgeGlobal = {
  configure: (config: TriedgeBridgeGlobalConfig) => void;
  readonly isConfigured: boolean;
  health: TriedgeBridgeHealth;
  uiAdapter: UiAdapter;
  parity: TriedgeParityBridge;
  version: string;
  buildTime: string;
};

const version = '1.0.0';
const buildTime = new Date().toISOString();

let configured = false;
const health: TriedgeBridgeHealth = {
  loadedAt: buildTime,
  configured: false,
  ready: false,
  uiAdapterReady: false,
  version,
};

const parity = createTriedgeParityBridge();

const updateHealth = (updates: Partial<TriedgeBridgeHealth>): void => {
  Object.assign(health, updates);
  if (typeof window !== 'undefined') {
    (window as any).TriedgeBridgeHealth = health;
  }
};

const bridge: TriedgeBridgeGlobal = {
  configure: (config) => {
    configureAmplify(config);
    const adapterConfig: Record<string, unknown> = {};
    if (typeof config.academicYear === 'string') adapterConfig.academicYear = config.academicYear;
    if (typeof config.useLocalStore === 'boolean') adapterConfig.useLocalStore = config.useLocalStore;
    if (typeof config.logEvents === 'boolean') adapterConfig.logEvents = config.logEvents;
    uiAdapter.configure(adapterConfig as any);
    configured = true;
    updateHealth({
      configured: true,
      ready: true,
      uiAdapterReady: true,
      configuredAt: new Date().toISOString(),
    });
  },
  get isConfigured() {
    return configured;
  },
  health,
  uiAdapter,
  parity,
  version,
  buildTime,
};

const looksLikeBridge = (value: unknown): value is TriedgeBridgeGlobal => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as any;
  return (
    typeof candidate.configure === 'function'
    && 'isConfigured' in candidate
    && candidate.uiAdapter
  );
};

export function installTriedgeBridgeGlobal(): TriedgeBridgeGlobal | undefined {
  if (typeof window === 'undefined') return undefined;

  const existing = (window as any).TriedgeBridge;
  if (looksLikeBridge(existing)) {
    if (!existing.parity) {
      existing.parity = parity;
    }
    return existing;
  }

  (window as any).TriedgeBridge = bridge;
  updateHealth({});
  return bridge;
}

export function ensureTriedgeBridgeGlobal(config: TriedgeBridgeGlobalConfig): TriedgeBridgeGlobal | undefined {
  const installed = installTriedgeBridgeGlobal();
  if (!installed) return undefined;
  if (!installed.isConfigured) {
    installed.configure(config);
  }
  return installed;
}
